import { useCallback, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Text } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';
import type { TableColumn, TableAction, BulkActionItem } from '@/components/ui/tables/Table';
import { checkInLocationService } from '@/api/domains/checkInLocation';
import type { CheckInLocationItem } from '@/api/domains/checkInLocation';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { useExport } from '@/hooks/ui/useExport';
import type { UIExportFormat } from '@/types/fileExport.types';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { formatToDdMmYyyy } from '@/utils';
import { ROUTE_DEFINITIONS, SCREEN_PREFERENCE_CODES } from '@/routes';


type LocationListFilters = {
  hasGeolocation?: boolean | null;
  isRemoteWork?: boolean | null;
};

export interface UseLocationListResult {
  rows: CheckInLocationItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<CheckInLocationItem>[];
  actions: TableAction<CheckInLocationItem>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  isLoading: boolean;
  searchPlaceholder: string;
  openCreatePanel: () => void;
  onDownload: (format: UIExportFormat) => Promise<void>;
  isExporting: boolean;
  filters: LocationListFilters;
  onFilterChange: (filters: LocationListFilters) => void;
  selectedRows: CheckInLocationItem[];
  onSelectionChange: (rows: CheckInLocationItem[]) => void;
  onBulkDisable: () => void;
  bulkActions: BulkActionItem[];
  tableSelectionKey: string;
  isSelectAllTotalActive: boolean;
  onSelectAllTotal: () => void;
  onClearSelectionTotal: () => void;
  canDelete: boolean;
}

export function useLocationList(): UseLocationListResult {
  const { t: translate } = useTranslation('checkInLocation');
  const { showApiError, showSuccess, showWarning, openSidePanel, openModal } = useUI();
  const { canCreate, canEdit, canDelete } = usePagePermissions();
  const navigate = useNavigate();

  const [selectedRows, setSelectedRows] = useState<CheckInLocationItem[]>([]);
  const [isSelectAllTotalActive, setIsSelectAllTotalActive] = useState(false);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<LocationListFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<CheckInLocationItem> | null> => {
      const response = await checkInLocationService.list(
        {
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          isActive: true,
          hasGeolocation: params.filters?.hasGeolocation ?? undefined,
          isRemoteWork: params.filters?.isRemoteWork ?? undefined,
          sort: params.sort
            ? { key: params.sort.key, direction: params.sort.direction.toLowerCase() as 'asc' | 'desc' }
            : undefined,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code !== 'ABORTED') {
          showApiError(response, translate('list.errors.loadError'));
        }
        return null;
      }

      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading: isLoading,
    onSearch,
    onSortChange,
    refetch,
    filters,
    setFilters,
    debouncedSearchText,
  } = usePaginatedList<CheckInLocationItem, LocationListFilters>({
    fetchFn,
    initialFilters: {},
    initialSort: { key: 'name', direction: 'ASC' },
    orderByMap: {
      id: 'locationId',
      name: 'name',
      address: 'address',
      geolocation: 'hasGeolocation',
      telework: 'isRemoteWork',
      createdAt: 'createdAt',
      status: 'isActive',
    },
    deduplicate: true,
    screenCode: SCREEN_PREFERENCE_CODES.UBICACIONES_LOCATIONS,
  });

  // Reset selection when page or page size changes
  useEffect(() => {
    setSelectedRows([]);
    setIsSelectAllTotalActive(false);
  }, [page, pageSize]);

  const [selectionVersion, setSelectionVersion] = useState(0);
  const tableSelectionKey = useMemo(() => `${page}-${pageSize}-${selectionVersion}`, [page, pageSize, selectionVersion]);

  const onSelectionChange = useCallback((selected: CheckInLocationItem[]) => {
    setSelectedRows(selected);
  }, []);

  const onSelectAllTotal = useCallback(() => {
    setIsSelectAllTotalActive(true);
  }, []);

  const onClearSelectionTotal = useCallback(() => {
    setIsSelectAllTotalActive(false);
    setSelectionVersion((v) => v + 1);
  }, []);

  const openCreatePanel = useCallback(() => {
    if (!canCreate) return;
    openSidePanel({
      title: translate('form.titleCreate'),
      component: 'LocationFormPanel',
      props: { onSubmit: refetch },
    });
  }, [canCreate, openSidePanel, translate, refetch]);

  const { onExport: onDownload, isExporting } = useExport<UIExportFormat>({
    exportFn: () => checkInLocationService.download(),
    errorMessage: translate('messages.downloadError'),
  });

  // Ubicaciones de la página actual deseleccionadas manualmente tras activar la selección total.
  const excludedLocationIds = useMemo(() => {
    if (!isSelectAllTotalActive) return [];
    const selectedIdSet = new Set(selectedRows.map((row) => row.id));
    return rows.filter((row) => !selectedIdSet.has(row.id)).map((row) => row.id);
  }, [isSelectAllTotalActive, rows, selectedRows]);

  const onBulkDisable = useCallback(() => {
    const activeFilters = {
      hasGeolocation: filters.hasGeolocation,
      isRemoteWork: filters.isRemoteWork,
      searchText: debouncedSearchText ?? null,
      excludeLocationIds: excludedLocationIds,
    };

    openModal('BulkDisableLocationModal', {
      items: selectedRows,
      isSelectAllMode: isSelectAllTotalActive,
      total,
      filters: activeFilters,
      onConfirm: async () => {
        const locationIds = selectedRows.map((row) => row.id);
        const response = await checkInLocationService.bulkDisable(
          locationIds,
          isSelectAllTotalActive,
          ...(isSelectAllTotalActive ? [activeFilters] : []),
        );

        if (!response.success || !response.data) {
          if (response.error?.code === 'CONFLICT_ALL_LOCATIONS_BLOCKED') {
            showApiError(response, translate('bulk.messages.disableAllBlocked'));
          } else {
            showApiError(response, translate('bulk.messages.disableError'));
          }
          return;
        }

        const result = response.data;
        if (result.isPartial) {
          const notFoundCount = result.items.filter(
            (item) => item.blockingReason === 'NOT_FOUND',
          ).length;
          const blockedByDependencyCount = result.totalBlocked - notFoundCount;

          if (notFoundCount > 0 && blockedByDependencyCount > 0) {
            showWarning(
              translate('bulk.messages.disablePartialWithNotFound', {
                totalDisabled: result.totalDeleted,
                totalBlocked: blockedByDependencyCount,
                totalNotFound: notFoundCount,
              }),
            );
          } else if (notFoundCount > 0) {
            showWarning(
              translate('bulk.messages.disablePartialWithNotFound', {
                totalDisabled: result.totalDeleted,
                totalBlocked: 0,
                totalNotFound: notFoundCount,
              }),
            );
          } else {
            showWarning(
              translate('bulk.messages.disablePartial', {
                totalDisabled: result.totalDeleted,
                totalBlocked: result.totalBlocked,
              }),
            );
          }
        } else {
          showSuccess(translate('bulk.messages.disableSuccess'));
        }

        setSelectedRows([]);
        setIsSelectAllTotalActive(false);
        setSelectionVersion((v) => v + 1);
        refetch();
      },
    });
  }, [
    selectedRows, isSelectAllTotalActive, total, filters, debouncedSearchText, excludedLocationIds,
    openModal, translate, showApiError, showWarning, showSuccess, refetch,
  ]);

  const bulkActions: BulkActionItem[] = useMemo(() => {
    if (!canDelete) return [];
    return [
      {
        label: translate('bulk.disable'),
        icon: 'close',
        onClick: onBulkDisable,
        variant: 'danger',
      },
    ];
  }, [canDelete, translate, onBulkDisable]);

  const columns: TableColumn<CheckInLocationItem>[] = useMemo(() => [
    {
      key: 'id',
      header: translate('list.columns.id'),
      sortable: true,
      render: (row: CheckInLocationItem) => String(row.id),
    },
    {
      key: 'name',
      header: translate('list.columns.name'),
      sortable: true,
      render: (row: CheckInLocationItem) => row.name,
    },
    {
      key: 'address',
      header: translate('list.columns.address'),
      sortable: true,
      render: (row: CheckInLocationItem) => row.address ?? translate('list.address.empty'),
    },
    {
      key: 'geolocation',
      header: translate('list.columns.geolocation'),
      sortable: true,
      render: (row: CheckInLocationItem) => (
        <Text
          variant="subheader-medium"
          color={row.hasGeolocation ? colors.feedback.success400 : colors.grays.neutral99}
          as="span"
        >
          {row.hasGeolocation ? translate('list.geolocation.yes') : translate('list.geolocation.no')}
        </Text>
      ),
    },
    {
      key: 'telework',
      header: translate('list.columns.telework'),
      sortable: true,
      render: (row: CheckInLocationItem) =>
        row.telework ? translate('list.telework.yes') : translate('list.telework.no'),
    },
    {
      key: 'createdAt',
      header: translate('list.columns.createdAt'),
      sortable: true,
      render: (row: CheckInLocationItem) => formatToDdMmYyyy(new Date(row.createdAt)),
    },
    {
      key: 'status',
      header: translate('list.columns.status'),
      sortable: true,
      render: (row: CheckInLocationItem) => (
        <Text
          variant="subheader-medium"
          color={row.status === 'active' ? colors.feedback.success400 : colors.grays.neutral99}
          as="span"
        >
          {row.status === 'active' ? translate('list.status.active') : translate('list.status.inactive')}
        </Text>
      ),
    },
  ], [translate]);

  const actions: TableAction<CheckInLocationItem>[] = useMemo(() => [
    {
      id: 'edit',
      label: translate('common:tableActions.edit'),
      icon: 'edit',
      hidden: (row) => !canEdit || row.status === 'inactive',
      onClick: (row) => {
        openSidePanel({
          title: translate('form.titleEdit'),
          component: 'LocationFormPanel',
          props: { record: row, onSubmit: refetch },
        });
      },
    },
    {
      id: 'geolocation',
      label: translate('actions.geolocation'),
      icon: 'externalLink',
      hidden: (row) => !canEdit || row.status === 'inactive',
      onClick: (row) => {
        void navigate(
          ROUTE_DEFINITIONS.UBICACIONES_GEOLOCALIZACION.path.replace(':locationId', String(row.id)),
          { state: { name: row.name, address: row.address, hasGeolocation: row.hasGeolocation } },
        );
      },
    },
    {
      id: 'delete',
      label: translate('common:tableActions.delete'),
      icon: 'close',
      hidden: (row) => !canDelete || row.status === 'inactive',
      variant: 'danger',
      onClick: (row) => {
        openModal('ConfirmModal', {
          variant: 'error',
          icon: 'close',
          title: translate('modal.delete.title'),
          bodyText: translate('modal.delete.body'),
          confirmText: translate('modal.delete.confirm'),
          onConfirm: async () => {
            const response = await checkInLocationService.delete(row.id);
            if (response.success) {
              showSuccess(translate('messages.deleteSuccess'));
              refetch();
            } else if (response.status === 409) {
              showApiError(response, translate('modal.delete.conflictError'));
            } else {
              showApiError(response, translate('messages.deleteError'));
            }
          },
        });
      },
    },
  ], [canEdit, canDelete, translate, openSidePanel, navigate, refetch, openModal, showSuccess,
    showApiError]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onSortChange,
    isLoading,
    searchPlaceholder: translate('list.searchPlaceholder'),
    openCreatePanel,
    onDownload,
    isExporting,
    filters,
    onFilterChange: setFilters,
    selectedRows,
    onSelectionChange,
    onBulkDisable,
    bulkActions,
    tableSelectionKey,
    isSelectAllTotalActive,
    onSelectAllTotal,
    onClearSelectionTotal,
    canDelete,
  };
}
